'use client';

import Link from 'next/link';
import { Minus, Plus, ShoppingBag } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useCartContext } from '@kit/shops/hooks';
import { Badge } from '@kit/ui/badge';
import { Button } from '@kit/ui/button';
import { Card, CardContent, CardFooter, CardHeader } from '@kit/ui/card';
import { Trans } from '@kit/ui/trans';

import type { Database } from '~/lib/database.types';
import { formatCurrency } from '~/lib/formatters';
import { getLocalizedText } from '~/lib/i18n/localized-text';

type ArticleRow = Database['public']['Tables']['shop_articles']['Row'];

interface ArticleCardProps {
  article: ArticleRow;
  currency: string;
  onBuy: () => void;
  onCardClick: () => void;
}

function getArticlePrice(article: ArticleRow, currency: string): number | null {
  if (currency === 'USD') return article.price_usd;
  return article.price_eur;
}

function isOutOfStock(article: ArticleRow): boolean {
  if (!article.track_inventory) return false;
  if (article.allow_backorder) return false;
  return (article.inventory_count ?? 0) <= 0;
}

export function ArticleCard({
  article,
  currency,
  onBuy,
  onCardClick,
}: ArticleCardProps) {
  const { t } = useTranslation();
  const title = getLocalizedText(article.title);
  const description = getLocalizedText(article.description);
  const price = getArticlePrice(article, currency);
  const outOfStock = isOutOfStock(article);

  const { items, addItem, updateQuantity } = useCartContext();
  const cartItem = items.find((i) => i.article_id === article.id);
  const cartQuantity = cartItem?.quantity ?? 0;

  // External URL articles use the old "Buy now" / "View" flow
  const isExternal = Boolean(article.external_url);

  function handleAddToCart(e: React.MouseEvent) {
    e.stopPropagation();
    addItem({
      article_id: article.id,
      quantity: 1,
      title,
      price: price ?? 0,
      image_url: article.featured_image_id
        ? `/api/media/${article.featured_image_id}`
        : null,
      shop_id: article.shop_id,
    });
  }

  return (
    <Card
      className="flex cursor-pointer flex-col overflow-hidden transition-shadow hover:shadow-md"
      onClick={onCardClick}
    >
      {/* Product image */}
      {article.featured_image_id ? (
        <div className="bg-muted relative aspect-[4/3] overflow-hidden">
          {/* eslint-disable-next-line @next/next/no-img-element -- /api/media endpoint 302s to Supabase signed / R2 media; image-rendering.md forbids next/image on media */}
          <img
            src={`/api/media/${article.featured_image_id}`}
            alt={title}
            loading="lazy"
            className="absolute inset-0 h-full w-full object-cover transition-transform duration-300 hover:scale-105"
          />
        </div>
      ) : (
        <div className="bg-muted flex aspect-[4/3] items-center justify-center">
          <ShoppingBag className="text-muted-foreground h-12 w-12" />
        </div>
      )}

      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-2">
          <h3 className="line-clamp-2 leading-snug font-semibold">{title}</h3>
          {article.is_subscription && (
            <Badge variant="secondary" className="shrink-0">
              <Trans i18nKey="shop:subscription" defaults="Subscription" />
            </Badge>
          )}
        </div>
      </CardHeader>

      <CardContent className="flex-1 pb-2">
        {description && (
          <p className="text-muted-foreground line-clamp-3 text-sm">
            {description}
          </p>
        )}
        {outOfStock && (
          <p className="text-destructive mt-2 text-xs font-medium">
            <Trans i18nKey="shop:outOfStock" defaults="Out of stock" />
          </p>
        )}
        <Link
          href={`/shop/${article.shop_id}/${article.slug ?? article.id}`}
          onClick={(e) => e.stopPropagation()}
          className="text-primary mt-3 inline-block text-sm hover:underline"
        >
          <Trans i18nKey="shop:viewProductPage" defaults="View product" />
        </Link>
      </CardContent>

      <CardFooter className="flex items-center justify-between pt-2">
        <div>
          {price !== null ? (
            <span className="text-lg font-bold">
              {article.is_subscription
                ? `${formatCurrency(price, currency)} / ${article.subscription_interval ?? 'month'}`
                : formatCurrency(price, currency)}
            </span>
          ) : (
            <span className="text-muted-foreground text-sm">
              <Trans i18nKey="shop:priceTBD" defaults="Price on request" />
            </span>
          )}
        </div>

        {/* External URL: navigate through the tracked /r/<redirect_code>
            redirect for attribution. Rendered as a real anchor so it works
            without JS and is crawlable; falls back to the onBuy() handler only
            when no redirect_code exists. */}
        {isExternal ? (
          article.redirect_code ? (
            <Button asChild size="sm" disabled={outOfStock || price === null}>
              <a
                href={`/r/${article.redirect_code}`}
                rel="nofollow sponsored"
                target="_blank"
                onClick={(e) => e.stopPropagation()}
              >
                <Trans i18nKey="shop:viewProduct" defaults="View" />
              </a>
            </Button>
          ) : (
            <Button
              size="sm"
              disabled={outOfStock || price === null}
              onClick={(e) => {
                e.stopPropagation();
                onBuy();
              }}
            >
              <Trans i18nKey="shop:viewProduct" defaults="View" />
            </Button>
          )
        ) : cartQuantity > 0 ? (
          /* Quantity controls when already in cart */
          <div
            className="flex items-center gap-1"
            onClick={(e) => e.stopPropagation()}
          >
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => updateQuantity(article.id, cartQuantity - 1)}
              aria-label={t('shop:decreaseQuantity', {
                defaultValue: 'Decrease quantity',
              })}
            >
              <Minus className="h-3 w-3" />
            </Button>
            <span className="w-6 text-center text-sm font-medium tabular-nums">
              {cartQuantity}
            </span>
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => updateQuantity(article.id, cartQuantity + 1)}
              aria-label={t('shop:increaseQuantity', {
                defaultValue: 'Increase quantity',
              })}
            >
              <Plus className="h-3 w-3" />
            </Button>
          </div>
        ) : (
          /* Add to cart button */
          <Button
            size="sm"
            disabled={outOfStock || price === null}
            onClick={handleAddToCart}
          >
            <Trans i18nKey="shop:addToCart" defaults="Add to cart" />
          </Button>
        )}
      </CardFooter>
    </Card>
  );
}
